import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "./_roadsCommon";

/** Ch 21.5 — Intersection / Roundabout designer. */
export default function IntersectionPanel({ projectId }) {
  const [tab, setTab] = useState("intersection"); // intersection | roundabout
  const [alignments, setAlignments] = useState([]);
  const [intersections, setIntersections] = useState([]);
  const [roundabouts, setRoundabouts] = useState([]);
  const [intForm, setIntForm] = useState({ alignment_id: "", chainage_m: 1000,
    type: "T-junction", main_road_width_m: 14,
    branch_road_width_m: 7, turning_radius_m: 12 });
  const [raForm, setRaForm] = useState({ name: "RA-1", center_x: 0, center_y: 0,
    inscribed_diameter_m: 36, circulatory_width_m: 9,
    arms: [
      { name: "N", bearing_deg: 0,   entry_width_m: 7, exit_width_m: 7 },
      { name: "E", bearing_deg: 90,  entry_width_m: 7, exit_width_m: 7 },
      { name: "S", bearing_deg: 180, entry_width_m: 7, exit_width_m: 7 },
      { name: "W", bearing_deg: 270, entry_width_m: 7, exit_width_m: 7 },
    ] });
  const [err, setErr] = useState("");

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  const reload = async () => {
    try {
      const [a, i, r] = await Promise.all([
        api.get(`/projects/${projectId}/roads/alignments`),
        api.get(`/projects/${projectId}/roads/intersections`),
        api.get(`/projects/${projectId}/roads/roundabouts`),
      ]);
      setAlignments(a.data.alignments || []);
      setIntersections(i.data.intersections || []);
      setRoundabouts(r.data.roundabouts || []);
      if (!intForm.alignment_id && a.data.alignments?.[0]) {
        setIntForm({ ...intForm, alignment_id: a.data.alignments[0].id });
      }
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const createIntersection = async () => {
    try {
      await api.post(`/projects/${projectId}/roads/intersections`, intForm);
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const createRoundabout = async () => {
    try {
      await api.post(`/projects/${projectId}/roads/roundabouts`, raForm);
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  const delInt = async (id) => {
    if (!window.confirm("Delete intersection?")) return;
    await api.delete(`/projects/${projectId}/roads/intersections/${id}`); reload();
  };
  const delRA = async (id) => {
    if (!window.confirm("Delete roundabout?")) return;
    await api.delete(`/projects/${projectId}/roads/roundabouts/${id}`); reload();
  };

  return (
    <div data-testid="intersection-panel" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
          <button data-testid="ix-tab-intersection"
            style={tab === "intersection" ? btn : btnGhost}
            onClick={() => setTab("intersection")}>Intersection</button>
          <button data-testid="ix-tab-roundabout"
            style={tab === "roundabout" ? btn : btnGhost}
            onClick={() => setTab("roundabout")}>Roundabout</button>
        </div>

        {tab === "intersection" ? (
          <div>
            <div style={label}>Alignment</div>
            <select data-testid="ix-alignment" style={inputStyle}
              value={intForm.alignment_id} onChange={(e) =>
                setIntForm({ ...intForm, alignment_id: e.target.value })}>
              {alignments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <div style={label}>Type</div>
            <select data-testid="ix-type" style={inputStyle}
              value={intForm.type} onChange={(e) =>
                setIntForm({ ...intForm, type: e.target.value })}>
              <option>T-junction</option><option>4-arm</option>
              <option>staggered</option><option>flyover</option>
            </select>
            <div style={label}>Chainage (m)</div>
            <input data-testid="ix-chainage" style={inputStyle} type="number"
              value={intForm.chainage_m} onChange={(e) =>
                setIntForm({ ...intForm, chainage_m: parseFloat(e.target.value) || 0 })} />
            <div style={label}>Main width / Branch width / Radius (m)</div>
            <div style={{ display: "flex", gap: 4 }}>
              <input data-testid="ix-main" style={inputStyle} type="number"
                value={intForm.main_road_width_m} onChange={(e) =>
                  setIntForm({ ...intForm, main_road_width_m: parseFloat(e.target.value) || 0 })} />
              <input data-testid="ix-branch" style={inputStyle} type="number"
                value={intForm.branch_road_width_m} onChange={(e) =>
                  setIntForm({ ...intForm, branch_road_width_m: parseFloat(e.target.value) || 0 })} />
              <input data-testid="ix-radius" style={inputStyle} type="number"
                value={intForm.turning_radius_m} onChange={(e) =>
                  setIntForm({ ...intForm, turning_radius_m: parseFloat(e.target.value) || 0 })} />
            </div>
            <button data-testid="ix-create" style={{ ...btn, width: "100%", marginTop: 12 }}
              onClick={createIntersection}>+ Create intersection</button>
          </div>
        ) : (
          <div>
            <div style={label}>Name</div>
            <input data-testid="ra-name" style={inputStyle}
              value={raForm.name} onChange={(e) => setRaForm({ ...raForm, name: e.target.value })} />
            <div style={label}>Center (x, y)</div>
            <div style={{ display: "flex", gap: 4 }}>
              <input data-testid="ra-cx" style={inputStyle} type="number"
                value={raForm.center_x} onChange={(e) =>
                  setRaForm({ ...raForm, center_x: parseFloat(e.target.value) || 0 })} />
              <input data-testid="ra-cy" style={inputStyle} type="number"
                value={raForm.center_y} onChange={(e) =>
                  setRaForm({ ...raForm, center_y: parseFloat(e.target.value) || 0 })} />
            </div>
            <div style={label}>Inscribed Ø (m) · Circulatory width (m)</div>
            <div style={{ display: "flex", gap: 4 }}>
              <input data-testid="ra-d" style={inputStyle} type="number"
                value={raForm.inscribed_diameter_m} onChange={(e) =>
                  setRaForm({ ...raForm, inscribed_diameter_m: parseFloat(e.target.value) || 0 })} />
              <input data-testid="ra-w" style={inputStyle} type="number"
                value={raForm.circulatory_width_m} onChange={(e) =>
                  setRaForm({ ...raForm, circulatory_width_m: parseFloat(e.target.value) || 0 })} />
            </div>
            <button data-testid="ra-create" style={{ ...btn, width: "100%", marginTop: 12 }}
              onClick={createRoundabout}>+ Create roundabout</button>
          </div>
        )}
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Intersections ({intersections.length})</div>
        {intersections.map((x) => (
          <div key={x.id} data-testid={`ix-row-${x.id}`} style={{ marginTop: 4, padding: 6,
                background: "rgba(255,255,255,0.04)", borderRadius: 4, fontSize: 11 }}>
            <b>{x.type}</b> @ chainage {x.chainage_m}m
            · main {x.main_road_width_m}m / branch {x.branch_road_width_m}m / R{x.turning_radius_m}m
            <button style={{ ...btnGhost, padding: "1px 6px", fontSize: 10, float: "right" }}
              onClick={() => delInt(x.id)}>×</button>
          </div>
        ))}

        <div style={{ ...label, marginTop: 16 }}>Roundabouts ({roundabouts.length})</div>
        {roundabouts.map((r) => (
          <div key={r.id} data-testid={`ra-row-${r.id}`} style={{ marginTop: 4, padding: 6,
                background: "rgba(255,255,255,0.04)", borderRadius: 4, fontSize: 11 }}>
            <b>{r.name}</b> · Ø {r.inscribed_diameter_m}m · {r.arms?.length} arms
            · R<sub>out</sub> {r.geometry.R_outer_m}m
            <button style={{ ...btnGhost, padding: "1px 6px", fontSize: 10, float: "right" }}
              onClick={() => delRA(r.id)}>×</button>
            <svg viewBox="-30 -30 60 60" width={120} height={120} style={{ display: "block",
                  margin: "8px auto", background: "#0a1220" }}>
              <circle cx={0} cy={0} r={r.geometry.R_outer_m / 2} fill="#1a2845" stroke="#F26B1F" strokeWidth={1} />
              <circle cx={0} cy={0} r={r.geometry.R_inner_m / 2} fill="#0a1220" stroke="#F26B1F" strokeWidth={1} />
              {r.arms.map((a, i) => {
                const rad = (a.bearing_deg * Math.PI) / 180;
                return <line key={i} x1={Math.sin(rad) * 25} y1={-Math.cos(rad) * 25}
                                x2={Math.sin(rad) * (r.geometry.R_outer_m / 2)}
                                y2={-Math.cos(rad) * (r.geometry.R_outer_m / 2)}
                                stroke="#3aa0ff" strokeWidth={2} />;
              })}
            </svg>
          </div>
        ))}
      </div>

      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Help</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          Intersections (T, 4-arm, staggered, flyover) are placed at chainage on a horizontal alignment.
          Roundabouts follow IRC SP-65 — supply inscribed diameter, circulatory width and arms with
          bearings + entry/exit widths. The system computes outer/inner radii and splitter island tip locations.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
